import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, Check, ChevronLeft, ChevronRight, MapPin, ShieldCheck } from "lucide-react";
import { API, CLINIC_SLOTS } from "./data";

function DateField({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [view, setView] = useState(() => (value ? new Date(value + "T00:00:00") : new Date()));

  useEffect(() => {
    if (value) { setOpen(false); setView(new Date(value + "T00:00:00")); }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const year = view.getFullYear(), month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = view.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const iso = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const display = value ? new Date(value + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <div className="date-field-wrap" ref={wrapRef}>
      <input className="form-input" data-testid="appointment-date-input" readOnly placeholder="Choose a date" value={display} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div className="cal-pop" data-testid="calendar-popup" onClick={(e) => e.preventDefault()}>
          <div className="cal-head">
            <button type="button" data-testid="cal-prev" onClick={() => setView(new Date(year, month - 1, 1))}><ChevronLeft size={16} /></button>
            <b>{monthName}</b>
            <button type="button" data-testid="cal-next" onClick={() => setView(new Date(year, month + 1, 1))}><ChevronRight size={16} /></button>
          </div>
          <div className="cal-grid">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <span key={i} className="cal-wd">{d}</span>)}
            {cells.map((d, i) => {
              if (!d) return <span key={i} />;
              const cellDate = new Date(year, month, d);
              const disabled = cellDate < today || cellDate.getDay() === 0;
              const selected = value === iso(d);
              return <button type="button" key={i} data-testid={`cal-day-${iso(d)}`} className={`cal-day${selected ? " selected" : ""}`} disabled={disabled} onClick={() => { onChange(iso(d)); setOpen(false); }}>{d}</button>;
            })}
          </div>
          <small className="cal-hint">Sundays are closed</small>
        </div>
      )}
    </div>
  );
}

export function AppointmentForm({ onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", clinic: "medicover", preferred_date: "", time_slot: "", concern: "" });
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(null);
  const update = (key) => (event) => {
    const value = event.target.value;
    if (key === "preferred_date" && value && new Date(value + "T00:00:00").getDay() === 0) {
      toast.error("The clinic is closed on Sundays", { description: "Please choose a Monday to Saturday date." });
      setForm({ ...form, preferred_date: "" });
      return;
    }
    setForm({ ...form, [key]: value });
  };
  const chooseClinic = (clinic) => setForm({ ...form, clinic, time_slot: "" });
  const submit = async (event) => {
    event.preventDefault();
    if (!form.preferred_date) { toast.error("Please choose a preferred date"); return; }
    setSending(true);
    try { await axios.post(`${API}/appointments`, form); toast.success("Appointment request received"); setBooked(form); }
    catch (error) { toast.error("Something went wrong", { description: "Please call 7780793371 and we'll help you directly." }); }
    finally { setSending(false); }
  };
  if (booked) {
    return <div className="booking-success" data-testid="booking-success">
      <span className="success-icon"><Check size={30} /></span>
      <h3>Appointment request received</h3>
      <p>Thank you, <b>{booked.name}</b>. Your request has been sent successfully.</p>
      <div className="success-summary">
        <span><MapPin size={14} /> {booked.clinic === "medicover" ? "MEDICOVER Hospitals, Hitec City" : "LUX Hospitals, Gachibowli"}</span>
        <span><CalendarDays size={14} /> {booked.preferred_date} · {CLINIC_SLOTS[booked.clinic].find(([v]) => v === booked.time_slot)?.[1] || booked.time_slot}</span>
      </div>
      <p className="success-note">For anything urgent, call <a href="tel:7780793371">7780793371</a>.</p>
      <button className="button button-primary full" data-testid="booking-done-button" onClick={onClose}>Done</button>
    </div>;
  }
  return <form className="appointment-form" onSubmit={submit} data-testid="appointment-form">
    <div className="form-heading"><span className="eyebrow">Start here</span><h3>Request an appointment</h3><p>Tell us a little about your visit.</p></div>
    <label>Your name<input data-testid="appointment-name-input" required value={form.name} onChange={update("name")} placeholder="Full name" /></label>
    <label>Phone number<input data-testid="appointment-phone-input" required type="tel" value={form.phone} onChange={update("phone")} placeholder="10-digit phone number" /></label>
    <span className="field-label">Choose clinic</span>
    <div className="clinic-choices">
      <button type="button" data-testid="clinic-choice-medicover" className={`clinic-choice ${form.clinic === "medicover" ? "active" : ""}`} onClick={() => chooseClinic("medicover")}>MEDICOVER Hospitals<small>Hitec City · Mon–Sat · 9 AM–4 PM</small></button>
      <button type="button" data-testid="clinic-choice-lux" className={`clinic-choice ${form.clinic === "lux" ? "active" : ""}`} onClick={() => chooseClinic("lux")}>LUX Hospitals<small>Gachibowli · 5–7 PM</small></button>
    </div>
    <label>Preferred date<DateField value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} /></label>
    <label>Preferred time<select data-testid="appointment-slot-select" required value={form.time_slot} onChange={update("time_slot")}><option value="" disabled>Select a time slot</option>{CLINIC_SLOTS[form.clinic].map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    <label>What would you like help with?<textarea data-testid="appointment-concern-input" required value={form.concern} onChange={update("concern")} placeholder="Briefly describe your concern" rows="3" /></label>
    <button className="button button-primary full" data-testid="appointment-submit-button" disabled={sending}>{sending ? "Sending request…" : "Send appointment request"}<ArrowRight size={17} /></button>
    <small className="form-note"><ShieldCheck size={14} /> Your details are kept private and used only to arrange your visit.</small>
  </form>;
}
